import React, { useState } from 'react';

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableFooter,
} from '@mui/material';

const ENTRIES = [
  {
    id: 'LRD-9910', status: 'error', currency: 'USD', supplier: 'Monterrey Auto Manufactura SA de CV',
    incoterm: 'FCA Nuevo Laredo', wireAmount: '$150,000.00 USD', expected: '$150,000.00 USD', declared: '$100,000.00 USD',
    fxStatus: '✓ Matched', variance: '-$50,000.00 USD (Under-Declared)',
    reason: 'CRITICAL TBML RISK: Financial wire transfer to Mexican supplier ($150k) exceeds the 7501 Customs Declared Value ($100k) by 50%. High probability of valuation fraud or Trade-Based Money Laundering. Broker protected from Form 28 liability.',
    txHash: 'PENDING_RESOLUTION_HALTED', txTime: 'N/A - FUNDS LOCKED',
  },
  {
    id: 'LRD-9911', status: 'clean', currency: 'USD', supplier: 'Saltillo Casting Corp',
    incoterm: 'DAP Laredo', wireAmount: '$85,000.00 USD', expected: '$82,000.00 USD', declared: '$82,000.00 USD',
    fxStatus: '✓ Matched', variance: '$0.00',
    reason: 'Reconciliation Complete: SWIFT wire matches CI minus deterministic DDP freight deduction. Declared value aligns perfectly with settled value.',
    txHash: '0x8f2a9c4b7e1d3f6a...', txTime: '2026-03-09 10:14:22 UTC',
  },
  {
    id: 'LRD-9912', status: 'warning', currency: 'MXN', supplier: 'Puebla Electronics SA',
    incoterm: 'FCA Puebla', wireAmount: '$1,200,000.00 MXN', expected: '$65,400.00 USD', declared: '$58,000.00 USD',
    fxStatus: '⚠️ FX Rate Mismatch', variance: 'Manual Review Required',
    reason: 'ACTION REQUIRED: Broker applied incorrect historical NY Fed MXN/USD exchange rate on the 7501, resulting in valuation under-reporting. Settlement paused until Form 7501 is amended.',
    txHash: 'PENDING_FX_CORRECTION', txTime: 'N/A - FUNDS LOCKED',
  },
  {
    id: 'LRD-9913', status: 'clean', currency: 'USD', supplier: 'Guadalajara Wiring Systems',
    incoterm: 'EXW Guadalajara', wireAmount: '$44,000.00 USD', expected: '$44,000.00 USD', declared: '$44,000.00 USD',
    fxStatus: '✓ Matched', variance: '$0.00',
    reason: 'Reconciliation Complete: Clean 3-way match. Funds released.',
    txHash: '0x3a1b4c9d8e7f2a5b...', txTime: '2026-03-09 11:02:45 UTC',
  },
];

const LLUSCBABroker = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(1);
  const [releaseState, setReleaseState] = useState({});

  const data = ENTRIES[selectedIndex];

  const handleSelectEntry = (index) => {
    setSelectedIndex(index);
    setActiveTab(1);
  };

  const getFxStatusClass = (currency) => currency === 'USD' ? 'llus-text-green' : 'llus-text-yellow';

  const getValueStatus = (status) => {
    if (status === 'error') return { text: '⚠️ CBP Audit Exposure', cls: 'llus-text-red' };
    if (status === 'warning') return { text: '⚠️ FX Discrepancy', cls: 'llus-text-yellow' };
    return { text: '✓ Ledger Reconciled', cls: 'llus-text-green' };
  };

  const getWalletBadge = (status) => {
    if (status === 'error') return { text: '🔒 SETTLEMENT HALTED (TBML RISK)', cls: 'llus-wallet-locked' };
    if (status === 'warning') return { text: '🔒 SETTLEMENT HALTED (FX MISMATCH)', cls: 'llus-wallet-locked' };
    return { text: '✅ FUNDS CLEARED FOR SETTLEMENT', cls: 'llus-wallet-cleared' };
  };

  const getCatchHeader = (entry) => {
    if (entry.status === 'error') return { text: `⚠️ Form 28 TBML Discrepancy: Entry #${entry.id}`, cls: 'llus-text-red' };
    if (entry.status === 'warning') return { text: `⚠️ Valuation Correction Required: Entry #${entry.id}`, cls: 'llus-text-yellow' };
    return { text: `✓ Cryptographic Audit Ledger: Entry #${entry.id}`, cls: 'llus-text-green' };
  };

  const getCatchCardClass = (status) => {
    if (status === 'error') return 'llus-highlight-row';
    if (status === 'warning') return 'llus-warning-row';
    return 'llus-clean-row';
  };

  const getVarClass = (status) => {
    if (status === 'error') return 'llus-text-red';
    if (status === 'warning') return 'llus-text-yellow';
    return 'llus-text-green';
  };

  const handleRelease = () => {
    setReleaseState({ processing: true });
    setTimeout(() => {
      setReleaseState({
        done: true,
        txHash: '0x' + Math.random().toString(16).slice(2, 18) + '...',
        txTime: new Date().toUTCString(),
      });
    }, 1200);
  };

  const released = releaseState.done;
  const processing = releaseState.processing && !released;
  const resolvedTxHash = released ? releaseState.txHash : data.txHash;
  const resolvedTxTime = released ? releaseState.txTime : data.txTime;

  const valueStatus = getValueStatus(data.status);
  const walletBadge = getWalletBadge(data.status);
  const catchHeader = getCatchHeader(data);

  const isLocked = data.status === 'error' || data.status === 'warning';

  const btnLabel = released
    ? '✓ Wire Released to Beneficiary'
    : processing
      ? 'Processing Cryptographic Hash...'
      : isLocked
        ? 'Settlement Locked - Resolve Discrepancy'
        : 'Authorize SWIFT Settlement';

  const btnClass = released || processing
    ? 'llus-primary-btn llus-btn-dark'
    : isLocked
      ? 'llus-primary-btn llus-btn-disabled'
      : 'llus-primary-btn llus-btn-green';

  return (
    <Box className="llus-wrapper">

      {/* Top Bar */}
      {/* <Box className="llus-topbar">
        <Typography className="llus-topbar-title">LLUSCBA Broker TBML Shield Broker TBML Shield</Typography>
        <Box className="llus-badge llus-badge-red">PORT OF LAREDO - LIVE ENVIRONMENT</Box>
      </Box> */}

      {/* Workspace */}
      <Box className="llus-workspace">

        {/* Queue Panel */}
        <Box
          className="llus-queue-panel"
          sx={{ opacity: activeTab === 3 ? 0.3 : 1, pointerEvents: activeTab === 3 ? 'none' : 'auto' }}
        >
          <Box className="llus-queue-header">
            <Typography className="llus-queue-header-title">Live Settlement Queue</Typography>
            <Typography className="llus-flagged-count">2 Suspended</Typography>
          </Box>

          {ENTRIES.map((entry, index) => {
            let dotClass = 'llus-dot-green';
            if (entry.status === 'error') dotClass = 'llus-dot-red';
            if (entry.status === 'warning') dotClass = 'llus-dot-yellow';

            return (
              <Box
                key={entry.id}
                className={`llus-queue-item${selectedIndex === index ? ' llus-selected' : ''}`}
                onClick={() => handleSelectEntry(index)}
              >
                <Box className="llus-q-id">
                  <span className={`llus-status-dot ${dotClass}`} />
                  {entry.id}
                </Box>
                <Box className="llus-q-supplier">{entry.supplier}</Box>
              </Box>
            );
          })}
        </Box>

        {/* Content Panel */}
        <Box className="llus-content-panel">

          {/* Tab Controls */}
          <Box className="llus-demo-controls">
            <button
              className={`llus-demo-btn${activeTab === 1 ? ' llus-active-tab' : ''}`}
              onClick={() => setActiveTab(1)}
            >
              1. 3-Way Reconciliation
            </button>
            <button
              className={`llus-demo-btn${activeTab === 2 ? ' llus-active-tab' : ''}`}
              onClick={() => setActiveTab(2)}
            >
              2. Form 28 Defense Ledger
            </button>
            <button
              className={`llus-demo-btn llus-analytics-tab${activeTab === 3 ? ' llus-active-tab' : ''}`}
              onClick={() => setActiveTab(3)}
            >
              3. Port Analytics
            </button>
          </Box>

          {/* Screen 1: 3-Way Reconciliation */}
          {activeTab === 1 && (
            <Box className="llus-flex-row">

              {/* Left card: Financial Track */}
              <Box className="llus-card llus-w-50">
                <Typography className="llus-card-title">Financial Track: Commercial Invoice Extract</Typography>
                <Box className="llus-doc-parse">
                  <span className="llus-parse-blue">[AI Ingestion: MEX_CI_Header]</span><br />
                  Beneficiary: {data.supplier}<br />
                  Incoterms: {data.incoterm}<br />
                  Currency: {data.currency}<br />
                  Port of Entry: 2304 (Laredo, TX)<br /><br />
                  <span className="llus-parse-blue">[AI Ingestion: Financial Settlement]</span><br />
                  Importer Wire Initiation: {data.wireAmount}<br />
                  SWIFT MT103 Status: PENDING LUSIDPAY APPROVAL<br /><br />
                  <span className="llus-parse-red">[AI Verification: Invoice Totals]</span><br />
                  Expected Customs Value: <strong>{data.expected}</strong>
                  <Box className="llus-source-docs">
                    <span className="llus-source-label">Source Documents (Secure S3 Vault)</span><br />
                    <button type="button" className="llus-doc-link">📄 Commercial Invoice (Extract_Source.pdf)</button>
                    <button type="button" className="llus-doc-link">📄 Bill of Lading / eManifest (BOL_Match.pdf)</button>
                    <button type="button" className="llus-doc-link">📄 Draft Form 7501 (EDI_Intercept.xml)</button>
                    <button type="button" className="llus-doc-link">📄 Packing List (PL_Weights_Dims.pdf)</button>
                  </Box>
                </Box>
              </Box>

              {/* Right card: Compliance Track */}
              <Box className="llus-card llus-w-50">
                <Typography className="llus-card-title">Compliance Track vs. Financial Track</Typography>
                <Table className="llus-table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="llus-th">Reconciliation Vector</TableCell>
                      <TableCell className="llus-th">ACE 7501 vs. CI/Bank</TableCell>
                      <TableCell className="llus-th">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className="llus-td">1. Supplier Identity</TableCell>
                      <TableCell className="llus-td">{data.supplier}</TableCell>
                      <TableCell className="llus-td llus-text-green">✓ Verified</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="llus-td">2. Incoterm Logic</TableCell>
                      <TableCell className="llus-td">{data.incoterm}</TableCell>
                      <TableCell className="llus-td llus-text-green">✓ Verified</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="llus-td">3. Currency / FX (19 CFR 159)</TableCell>
                      <TableCell className="llus-td">{data.currency}</TableCell>
                      <TableCell className={`llus-td ${getFxStatusClass(data.currency)}`}>{data.fxStatus}</TableCell>
                    </TableRow>
                    <TableRow className="llus-highlight-value-row">
                      <TableCell className="llus-td"><strong>4. Value Reconciliation</strong></TableCell>
                      <TableCell className="llus-td"><strong>{data.expected} (CI) vs {data.declared} (7501)</strong></TableCell>
                      <TableCell className={`llus-td ${valueStatus.cls}`}>{valueStatus.text}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <Box className="llus-wallet-section">
                  <Typography className="llus-wallet-label">Restricted Wallet Smart Contract:</Typography>
                  <Box className={`llus-wallet-status ${walletBadge.cls}`}>
                    {released ? '✅ FUNDS SETTLED' : walletBadge.text}
                  </Box>
                  <Box sx={{ marginTop: '12px' }}>
                    <button
                      type="button"
                      className={btnClass}
                      onClick={!isLocked && !released && !processing ? handleRelease : undefined}
                      disabled={isLocked || released || processing}
                    >
                      {btnLabel}
                    </button>
                  </Box>
                </Box>
              </Box>

            </Box>
          )}

          {/* Screen 2: Form 28 Defense Ledger */}
          {activeTab === 2 && (
            <Box className={`llus-card ${getCatchCardClass(data.status)}`}>
              <Typography className={`llus-catch-header ${catchHeader.cls}`}>
                {catchHeader.text}
              </Typography>
              <Table className="llus-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className="llus-th">CBP 7501 Line Item Category</TableCell>
                    <TableCell className="llus-th">Broker Declared (7501)</TableCell>
                    <TableCell className="llus-th">Financial Settlement (Bank Wire)</TableCell>
                    <TableCell className="llus-th">Financial vs. Declared Discrepancy</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell className="llus-td">Automotive Components (Assorted)</TableCell>
                    <TableCell className="llus-td">{data.declared}</TableCell>
                    <TableCell className="llus-td">{data.wireAmount}</TableCell>
                    <TableCell className={`llus-td ${getVarClass(data.status)}`}>{data.variance}</TableCell>
                  </TableRow>
                </TableBody>
                <TableFooter />
              </Table>
              <Typography className="llus-catch-reason">{data.reason}</Typography>
              <Box className="llus-crypto-box">
                <strong>Cryptographic Audit Trail (Form 28 Proof):</strong><br />
                TxHash: {resolvedTxHash}<br />
                Timestamp: {resolvedTxTime}
              </Box>
            </Box>
          )}

          {/* Screen 3: Port Analytics */}
          {activeTab === 3 && (
            <Box>
              <Box className="llus-metric-grid">
                <Box className="llus-metric-card llus-metric-green">
                  <Typography className="llus-metric-label">TOTAL FUNDS CLEARED FOR SETTLEMENT</Typography>
                  <Typography className="llus-metric-value llus-text-green">$8,452,100.00</Typography>
                </Box>
                <Box className="llus-metric-card llus-metric-blue">
                  <Typography className="llus-metric-label">LAREDO ENTRIES PROCESSED (TODAY)</Typography>
                  <Typography className="llus-metric-value llus-text-blue">142</Typography>
                </Box>
                <Box className="llus-metric-card llus-metric-red">
                  <Typography className="llus-metric-label">TBML / VALUATION ANOMALIES BLOCKED</Typography>
                  <Typography className="llus-metric-value llus-text-red">2</Typography>
                </Box>
              </Box>
              <Box className="llus-export-center">
                <button type="button" className="llus-primary-btn llus-btn-dark">
                  ⬇ Generate CBP Form 28 Defense Dossier
                </button>
                <Typography className="llus-export-sub">
                  Exports cryptographic proof of valuation for U.S. Customs and Border Protection.
                </Typography>
              </Box>
            </Box>
          )}

        </Box>
      </Box>
    </Box>
  );
};

export default LLUSCBABroker;
